import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Mail,
  MessageSquare,
  BookOpen,
  Users,
  Activity,
  CheckCircle2,
  Send,
  Clock,
} from "lucide-react";
import Navigation from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";

const SUBJECTS = [
  "Sales",
  "Billing",
  "Technical",
  "Compliance",
  "Partnership",
  "Other",
];

const SUPPORT_EMAIL = "support@voxinity.ai";
const COMMUNITY_URL = "https://discord.gg/cUgxTYnpM";

const SupportPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Sales",
    inquiry: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.inquiry.trim().length < 10) {
      setError("Please describe your inquiry (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      await axios.post(`${apiBase}/api/support/inquiry`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        inquiry: form.inquiry.trim(),
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not send your inquiry. Please email us directly at support@voxinity.ai."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="support-page"
    >
      <Navigation />

      <main className="relative pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <section className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-400/10 border border-teal-400/20 text-xs text-teal-300 font-medium mb-6">
              We're here to help
            </div>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl text-white mb-5 leading-tight">
              Contact Voxinity Support
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Most questions are answered in the{" "}
              <Link to="/help" className="text-teal-300 hover:underline">
                Help Center
              </Link>{" "}
              — but if you need a human, drop us a note below. We reply within{" "}
              <span className="text-white">1 business day</span>.
            </p>
          </section>

          {/* Quick links */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto mb-14">
            <Link
              to="/help"
              className="group p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
              data-testid="support-quick-help-center"
            >
              <BookOpen className="text-teal-300 mb-3" size={20} />
              <div className="text-white font-semibold mb-1">Help Center</div>
              <div className="text-sm text-gray-400">
                Step-by-step guides for every feature.
              </div>
            </Link>
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
              data-testid="support-quick-community"
            >
              <Users className="text-teal-300 mb-3" size={20} />
              <div className="text-white font-semibold mb-1">Private Community</div>
              <div className="text-sm text-gray-400">
                Join the private Voxinity AI community on Discord.
              </div>
            </a>
            <Link
              to="/status"
              className="group p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
              data-testid="support-quick-status"
            >
              <Activity className="text-teal-300 mb-3" size={20} />
              <div className="text-white font-semibold mb-1">System Status</div>
              <div className="text-sm text-gray-400">
                Live uptime for every Voxinity service.
              </div>
            </Link>
          </section>

          {/* Main grid: form + sidebar */}
          <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 max-w-5xl mx-auto">
            {/* Form */}
            <div className="p-7 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
              {submitted ? (
                <div
                  className="text-center py-12"
                  data-testid="support-form-success"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="text-emerald-300" size={28} />
                  </div>
                  <h2 className="font-display font-semibold text-2xl text-white mb-2">
                    Got it — thanks!
                  </h2>
                  <p className="text-gray-400 max-w-md mx-auto">
                    We received your message and will reply to{" "}
                    <span className="text-white">{form.email}</span> within 1
                    business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        phone: "",
                        subject: "Sales",
                        inquiry: "",
                      });
                    }}
                    className="mt-6 text-sm text-teal-300 hover:text-teal-200 transition-colors"
                    data-testid="support-form-send-another"
                  >
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="support-form"
                >
                  <h2 className="font-display font-semibold text-2xl text-white">
                    Send us a message
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="support-name"
                        className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                      >
                        Name *
                      </label>
                      <input
                        id="support-name"
                        type="text"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Jane Smith"
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-gray-500 focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all"
                        data-testid="support-form-name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="support-email"
                        className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                      >
                        Email *
                      </label>
                      <input
                        id="support-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder="you@business.com"
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-gray-500 focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all"
                        data-testid="support-form-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="support-phone"
                        className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                      >
                        Phone (optional)
                      </label>
                      <input
                        id="support-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        placeholder="+1 (555) 555-0123"
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-gray-500 focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all"
                        data-testid="support-form-phone"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="support-subject"
                        className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                      >
                        Topic *
                      </label>
                      <select
                        id="support-subject"
                        value={form.subject}
                        onChange={(e) =>
                          setForm({ ...form, subject: e.target.value })
                        }
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all"
                        data-testid="support-form-subject"
                      >
                        {SUBJECTS.map((s) => (
                          <option
                            key={s}
                            value={s}
                            className="bg-space-900 text-white"
                          >
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="support-inquiry"
                      className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                    >
                      How can we help? *
                    </label>
                    <textarea
                      id="support-inquiry"
                      required
                      minLength={10}
                      rows={6}
                      value={form.inquiry}
                      onChange={(e) =>
                        setForm({ ...form, inquiry: e.target.value })
                      }
                      placeholder="Tell us what's going on. The more detail, the faster we can help (workspace name, agent ID, error messages, etc.)."
                      className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-gray-500 focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all resize-y"
                      data-testid="support-form-inquiry"
                    />
                    <div className="text-xs text-gray-500 mt-1.5">
                      {form.inquiry.length}/4000 characters
                    </div>
                  </div>

                  {error && (
                    <div
                      className="px-4 py-3 rounded-xl border border-rose-400/30 bg-rose-400/[0.06] text-rose-200 text-sm"
                      data-testid="support-form-error"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-teal-400 hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed text-space-900 font-medium transition-colors"
                    data-testid="support-form-submit"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Inquiry <Send size={14} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-4">
              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                <Mail className="text-teal-300 mb-3" size={20} />
                <div className="text-white font-semibold mb-1">Email us</div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-teal-300 hover:underline text-sm"
                  data-testid="support-sidebar-email"
                >
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-xs text-gray-500 mt-2">
                  Use the form for the fastest response — emails are routed to
                  the same inbox.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                <Clock className="text-teal-300 mb-3" size={20} />
                <div className="text-white font-semibold mb-1">
                  Response times
                </div>
                <ul className="text-sm text-gray-400 space-y-2 mt-2">
                  <li className="flex items-center justify-between">
                    <span>General</span>
                    <span className="text-white">≤ 24 hrs</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span>Agency / Pro</span>
                    <span className="text-white">≤ 4 hrs</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span>Lifetime</span>
                    <span className="text-white">Priority</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span>Outage / P0</span>
                    <span className="text-emerald-300">{"<"} 1 hr</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                <MessageSquare className="text-teal-300 mb-3" size={20} />
                <div className="text-white font-semibold mb-1">
                  Active customers
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Logged-in users get a faster track — open the in-app{" "}
                  <span className="text-white">Feedback</span> widget instead of
                  this form and we'll attribute the ticket to your workspace
                  automatically.
                </p>
              </div>
            </aside>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SupportPage;
